// src/features/commands/CommandsPage.tsx
import { useState, useEffect, type ChangeEvent } from 'react';
import { useGuild } from '../../lib/GuildContext';
import { useCommands, useSaveCommand, type CommandOverride } from './useCommands';
import { useUpdateCooldownOverrides } from './useCooldownOverrides';
import './CommandsPage.css';

// Mirrors commands.js's CUSTOM_RESPONSE_RE exactly, for immediate
// client-side feedback — the server remains the actual source of
// truth and re-validates independently.
const CUSTOM_RESPONSE_RE = /^[\w\s\-_:.,!?'"()\n]{0,500}$/u;

function CommandRow({ guildId, command, index }: { guildId: string; command: CommandOverride; index: number }) {
  const [customResponse, setCustomResponse] = useState(command.custom_response ?? '');
  // Only meaningful on narrow screens — see .cmd-row__inline-message /
  // .cmd-row__expand-btn CSS. On wide screens the inline input is
  // always shown and this is unused; on narrow screens it drives the
  // one input that exists there.
  const [expanded, setExpanded] = useState(false);
  const save = useSaveCommand();

  const isDirty = customResponse !== (command.custom_response ?? '');
  const isValid = CUSTOM_RESPONSE_RE.test(customResponse);

  function toggleEnabled() {
    save.mutate({
      guildId,
      commandName: command.command_name,
      enabled: !command.enabled,
      customResponse: command.custom_response,
    });
  }

  // Auto-saves on blur rather than requiring an explicit Save button —
  // matches Channels' settings and Owner Admin's report notes. Only
  // fires if the value actually changed and passes validation.
  function handleBlur() {
    if (!isDirty || !isValid) return;
    save.mutate({
      guildId,
      commandName: command.command_name,
      enabled: command.enabled,
      customResponse: customResponse || null,
    });
  }

  const messageInputProps = {
    className: 'input',
    type: 'text' as const,
    value: customResponse,
    onChange: (e: ChangeEvent<HTMLInputElement>) => setCustomResponse(e.target.value),
    onBlur: handleBlur,
    placeholder: 'Default warning message',
    maxLength: 500,
  };

  return (
    <>
      <div className={`cmd-row${index % 2 === 1 ? ' zebra-row--even' : ''}`}>
        <span className="cmd-row__name">/{command.command_name}</span>

        {/* Wide screens: message input shown inline, always — no click
            needed, matches the extra width the 2/3 column now has. */}
        <div className="cmd-row__inline-message">
          <input {...messageInputProps} />
        </div>

        <div className="cmd-row__actions">
          {/* Narrow screens only (CSS-hidden on wide) — there isn't
              room for the input inline, so it collapses behind a click
              instead, same as before. */}
          <button
            type="button"
            className="cmd-row__expand-btn"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
          >
            message
          </button>
          <button
            className={`toggle${command.enabled ? ' toggle--on' : ''}`}
            role="switch"
            aria-checked={command.enabled}
            aria-label={`${command.enabled ? 'Disable' : 'Enable'} /${command.command_name}`}
            onClick={toggleEnabled}
            disabled={save.isPending}
          >
            <span className="toggle__thumb" />
          </button>
        </div>
      </div>

      {/* Narrow-screen-only detail row, CSS-hidden on wide screens
          regardless of `expanded` (the inline version above is used
          there instead). */}
      {expanded && (
        <div className="cmd-row-detail">
          <input {...messageInputProps} />
        </div>
      )}

      {!isValid && (
        <p className="commands-table__error">Only letters, numbers, and basic punctuation are allowed.</p>
      )}
    </>
  );
}

function CooldownRow({
  name, value, onChange, onBlur, valid, index,
}: {
  name: string; value: string; onChange: (v: string) => void; onBlur: () => void; valid: boolean; index: number;
}) {
  return (
    <div className={`cmd-row${index % 2 === 1 ? ' zebra-row--even' : ''}`}>
      <span className="cmd-row__name">/{name}</span>
      <div>
        <input
          className="cooldown-input"
          type="number"
          min="1"
          max="300"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          placeholder="Default"
        />
        {!valid && <p className="commands-table__error">1-300s</p>}
      </div>
    </div>
  );
}

function CooldownOverridesSection({ guildId, commandNames }: { guildId: string; commandNames: string[] }) {
  const { guilds } = useGuild();
  const guild = guilds.find((g) => g.guild_id === guildId);
  const update = useUpdateCooldownOverrides(guildId);

  const [overrides, setOverrides] = useState<Record<string, string>>({}); // commandName -> seconds, as typed
  const [savedValues, setSavedValues] = useState<Record<string, string>>({}); // last-saved values, to detect real changes on blur

  useEffect(() => {
    if (!guild) return;
    const existing = (guild.settings.cooldownOverrides as Record<string, number> | undefined) ?? {};
    const seconds: Record<string, string> = {};
    for (const [cmd, ms] of Object.entries(existing)) {
      seconds[cmd] = String(ms / 1000);
    }
    setOverrides(seconds);
    setSavedValues(seconds);
  }, [guild]);

  function handleChange(commandName: string, value: string) {
    setOverrides((prev) => ({ ...prev, [commandName]: value }));
  }

  function isEntryValid(value: string): boolean {
    if (value === '') return true;
    const n = Number(value);
    return Number.isFinite(n) && n >= 1 && n <= 300;
  }

  function handleBlur(commandName: string) {
    if (overrides[commandName] === (savedValues[commandName] ?? '')) return;
    const allValid = (Object.values(overrides) as string[]).every((v) => isEntryValid(v));
    if (!allValid) return;

    const result: Record<string, number> = {};
    for (const [cmd, secondsStr] of Object.entries(overrides)) {
      if (secondsStr === '') continue;
      result[cmd] = Number(secondsStr) * 1000;
    }
    update.mutate(result, {
      onSuccess: () => setSavedValues(overrides),
    });
  }

  return (
    <article className="card scrollable-card">
      <header className="card__header">
        <div>
          <p className="card__eyebrow">Advanced</p>
          <h2 className="card__title">Cooldown overrides</h2>
        </div>
      </header>
      <p className="card__hint">
        How long a member must wait between uses of a command, in seconds. Leave a field blank to
        use that command&apos;s default cooldown. Values must be between 1 and 300 seconds.
      </p>
      <div className="scrollable-card__scroll">
        {commandNames.map((name, index) => (
          <CooldownRow
            key={name}
            name={name}
            value={overrides[name] ?? ''}
            onChange={(v) => handleChange(name, v)}
            onBlur={() => handleBlur(name)}
            valid={isEntryValid(overrides[name] ?? '')}
            index={index}
          />
        ))}
      </div>
      {update.isError && (
        <p className="card__error">Couldn&apos;t save. Try again.</p>
      )}
    </article>
  );
}

export function CommandsPage() {
  const { selectedGuildId } = useGuild();
  const { data: commands, isLoading, error } = useCommands(selectedGuildId);

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">Server commands</p>
        <h1 className="page-header__title">Commands</h1>
      </div>

      {isLoading && <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Loading commands…</p>}
      {error && <p style={{ color: 'var(--negative)', fontSize: 13 }}>Couldn&apos;t load commands. Try refreshing.</p>}

      {commands && selectedGuildId && (
        <div className="commands-columns">
          <article className="card scrollable-card">
            <header className="card__header">
              <div>
                <p className="card__eyebrow">{commands.length} commands</p>
                <h2 className="card__title">Access &amp; warning messages</h2>
              </div>
            </header>
            <p className="card__hint">
              Turning a command off disables it in this server only — members will see a warning
              message instead of it running. Click &quot;message&quot; on any row to set your own
              wording, or leave it blank to use the default.
            </p>
            <div className="scrollable-card__scroll">
              {commands.map((command, index) => (
                <CommandRow key={command.command_name} guildId={selectedGuildId} command={command} index={index} />
              ))}
            </div>
          </article>

          <CooldownOverridesSection
            guildId={selectedGuildId}
            commandNames={commands.map((c) => c.command_name)}
          />
        </div>
      )}
    </>
  );
}
